/* eslint-disable prettier/prettier */
import { GridOptions } from 'ag-grid-community'
import _ from 'lodash'
import { intl } from '../../../../i18n'
import { DefaultCellRenderer } from '../../../containers/BulkSheetView/components/cellRenderer'
import IconCellRenderer from '../../../containers/commons/AgGrid/components/cell/common/iconCell'
import {
  ColumnType,
  columnTypes,
  dateValueFormatter,
  defaultOnCellClicked,
  frameworkComponents,
} from '../../../containers/commons/AgGrid'
import { SequenceNoCellRenderer } from '../../../containers/commons/AgGrid/components/cell/custom/sequenceNo/SequenceNoCellRenderer'
import { DISPLAY_DATE_FORMAT } from '../../../../utils/date'
import { ClientSideNumberFilter } from '../../../containers/BulkSheetView/components/filter'

export const projectClosingStatusGridOptions = (): GridOptions => {
  return {
    groupHeaderHeight: 25,
    headerHeight: 45,
    floatingFiltersHeight: 25,
    rowHeight: 32,
    treeData: false,
    context: {},
    suppressLastEmptyLineOnPaste: true,
    suppressContextMenu: true,
    // Row
    rowDragManaged: false,
    rowDragMultiRow: false,
    suppressMoveWhenRowDragging: false,
    enterMovesDownAfterEdit: true,
    // Column
    columnTypes: columnTypes(),
    components: frameworkComponents,
    defaultColDef: {
      width: 85,
      editable: false,
      enableValue: false,
      sortable: true,
      resizable: true,
      suppressMenu: true,
      suppressSizeToFit: true,
      singleClickEdit: false,
      cellEditor: 'textEditor',
      cellRenderer: DefaultCellRenderer,
      onCellClicked: defaultOnCellClicked,
      suppressPaste: params => !_.isEmpty(params.context?.copied),
    },
    columnDefs: [
      {
        field: 'rowNumber',
        type: [ColumnType.sequenceNo],
        resizable: false,
        width: 35,
        pinned: 'left',
        cellRenderer: SequenceNoCellRenderer,
        valueGetter: params => {
          return (params.node?.rowIndex ?? 0) + 1
        },
      },
      {
        field: 'division',
        headerName: intl.formatMessage({
          id: 'projectClosingStatus.division',
        }),
        hide: false,
        pinned: 'left',
        width: 140,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
      },
      {
        field: 'project',
        headerName: intl.formatMessage({ id: 'projectClosingStatus.project' }),
        pinned: 'left',
        hide: false,
        editable: false,
        width: 180,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        cellRenderer: IconCellRenderer,
        cellRendererParams: {
          labelField: 'project.name',
          iconUrlField: 'project.iconUrl',
        },
        valueGetter: params => {
          return params.data?.project?.name || ''
        },
      },
      {
        field: 'projectStatus',
        headerName: intl.formatMessage({
          id: 'projectClosingStatus.projectStatus',
        }),
        pinned: 'left',
        hide: false,
        editable: false,
        width: 170,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
      },
      {
        field: 'startDate',
        headerName: intl.formatMessage({
          id: 'projectClosingStatus.startDate',
        }),
        cellStyle: { justifyContent: 'flex-end' },
        pinned: 'left',
        hide: false,
        editable: false,
        width: 120,
        type: [ColumnType.dateTime],
        floatingFilter: true,
        valueFormatter: params =>
          dateValueFormatter(params.value, DISPLAY_DATE_FORMAT) || '',
      },
      {
        field: 'endDate',
        headerName: intl.formatMessage({
          id: 'projectClosingStatus.endDate',
        }),
        cellStyle: { justifyContent: 'flex-end' },
        pinned: 'left',
        hide: false,
        editable: false,
        width: 120,
        type: [ColumnType.dateTime],
        floatingFilter: true,
        valueFormatter: params =>
          dateValueFormatter(params.value, DISPLAY_DATE_FORMAT) || '',
      },
      {
        field: 'projectManager',
        headerName: intl.formatMessage({
          id: 'projectClosingStatus.projectManager',
        }),
        pinned: 'left',
        hide: false,
        editable: false,
        width: 170,
        cellRenderer: IconCellRenderer,
        cellRendererParams: {
          labelField: 'projectManager.name',
          iconUrlField: 'projectManager.iconUrl',
        },
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        valueGetter: params => {
          return params.data?.projectManager?.name || ''
        },
      },
      {
        field: 'closingStatus',
        headerName: intl.formatMessage({
          id: 'projectClosingStatus.closingStatus',
        }),
        pinned: 'left',
        hide: false,
        editable: false,
        width: 120,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        valueGetter: params => {
          return getProjectClosingStatusLabel(params.data.closingStatus)
        },
      },
      {
        field: 'userCount',
        headerName: intl.formatMessage({
          id: 'projectClosingStatus.userCount',
        }),
        hide: false,
        editable: false,
        width: 100,
        filter: ClientSideNumberFilter,
        floatingFilter: true,
        cellClass: 'text-right',
      },
      {
        field: 'approvedUserCount',
        headerName: intl.formatMessage({
          id: 'projectClosingStatus.approvedUserCount',
        }),
        hide: false,
        editable: false,
        width: 100,
        filter: ClientSideNumberFilter,
        floatingFilter: true,
      },
      {
        field: 'closingDate',
        headerName: intl.formatMessage({
          id: 'projectClosingStatus.closingDate',
        }),
        cellStyle: { justifyContent: 'flex-end' },
        hide: false,
        editable: false,
        width: 120,
        floatingFilter: true,
        type: [ColumnType.dateTime],
        valueFormatter: params => {
          if (!params.value) return ''
          const date = new Date(params.value)
          return date
            .toLocaleString('ja-JP', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            })
            .replace(/\//g, '/')
            .replace(/,/, '')
        },
      },
      {
        field: 'closer',
        headerName: intl.formatMessage({
          id: 'projectClosingStatus.closer',
        }),
        hide: false,
        editable: false,
        width: 170,
        cellRenderer: IconCellRenderer,
        cellRendererParams: {
          labelField: 'closer.name',
          iconUrlField: 'closer.iconUrl',
        },
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        valueGetter: params => {
          return params.data?.closer?.name || ''
        },
      },
    ],
  }
}
export const getProjectClosingStatusLabel = (status: boolean) => {
  return status
    ? intl.formatMessage({ id: 'projectClosingStatus.closed' })
    : intl.formatMessage({ id: 'projectClosingStatus.open' })
}
